import CloseIcon from '@mui/icons-material/Close'
import {
  Alert,
  AlertColor,
  AlertTitle,
  Box,
  IconButton,
  Slide,
  SlideProps,
  Snackbar,
  SnackbarOrigin,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'

// ==========================================
// 1. TIPOS Y DEFINICIONES
// ==========================================

type HorizontalPos = 'left' | 'center' | 'right'
type VerticalPos = 'top' | 'bottom'
export type ToastPosition = `${HorizontalPos}_${VerticalPos}`

export interface ToastOptions {
  position?: ToastPosition
  duration?: number
  title?: string | React.ReactNode
  action?: React.ReactNode
}

export interface ToastProviderProps {
  children: React.ReactNode
  defaultOptions?: ToastOptions
}

export interface ToastContextType {
  toast: {
    (message: React.ReactNode, options?: ToastOptions & { severity?: AlertColor }): void
    info: (message: React.ReactNode, options?: ToastOptions) => void
    success: (message: React.ReactNode, options?: ToastOptions) => void
    warning: (message: React.ReactNode, options?: ToastOptions) => void
    error: (message: React.ReactNode, options?: ToastOptions) => void
    dismiss: () => void
  }
}

// ==========================================
// 2. CONTEXTO Y HOOK PERSONALIZADO
// ==========================================

const ToastContext = createContext<ToastContextType | undefined>(undefined)

export const useToast = () => {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast debe usarse dentro de un ToastProvider')
  }
  return context
}

// Componentes de transición para el Slider dependiendo de la dirección
const SlideTransitionLeft = (props: SlideProps) => <Slide {...props} direction="left" />
const SlideTransitionRight = (props: SlideProps) => <Slide {...props} direction="right" />
const SlideTransitionUp = (props: SlideProps) => <Slide {...props} direction="up" />
const SlideTransitionDown = (props: SlideProps) => <Slide {...props} direction="down" />

// ==========================================
// 3. TOAST PROVIDER (El motor del sistema)
// ==========================================

interface ToastConfig {
  message: React.ReactNode
  severity: AlertColor
  position: ToastPosition
  duration: number
  title?: string | React.ReactNode
  action?: React.ReactNode
}

export const ToastProvider = ({ children, defaultOptions }: ToastProviderProps) => {
  const [open, setOpen] = useState(false)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  // Valores por defecto globales
  const fallbackPosition = defaultOptions?.position || 'right_bottom'
  const fallbackDuration = defaultOptions?.duration ?? 3000

  // Configuración del toast actual
  const [toastConfig, setToastConfig] = useState<ToastConfig>({
    message: '',
    severity: 'info',
    position: fallbackPosition,
    duration: fallbackDuration,
  })

  // Estado para controlar el tiempo. Si es null, el Toast no se cierra automáticamente.
  const [timer, setTimer] = useState<number | null>(fallbackDuration)

  // Función base que procesa todos los toasts
  const baseShowToast = useCallback(
    (message: React.ReactNode, options?: ToastOptions & { severity?: AlertColor }) => {
      const duration = options?.duration ?? fallbackDuration

      setToastConfig({
        message,
        severity: options?.severity || 'info',
        position: options?.position || fallbackPosition,
        duration,
        title: options?.title,
        action: options?.action,
      })

      setTimer(duration)
      setOpen(true)
    },
    [fallbackPosition, fallbackDuration],
  )

  // Construimos el objeto toast con sus submétodos usando useMemo
  const toast = useMemo(() => {
    const toastFn = (message: React.ReactNode, options?: ToastOptions & { severity?: AlertColor }) =>
      baseShowToast(message, options)

    toastFn.info = (message: React.ReactNode, options?: ToastOptions) =>
      baseShowToast(message, { ...options, severity: 'info' })
    toastFn.success = (message: React.ReactNode, options?: ToastOptions) =>
      baseShowToast(message, { ...options, severity: 'success' })
    toastFn.warning = (message: React.ReactNode, options?: ToastOptions) =>
      baseShowToast(message, { ...options, severity: 'warning' })
    toastFn.error = (message: React.ReactNode, options?: ToastOptions) =>
      baseShowToast(message, { ...options, severity: 'error' })
    toastFn.dismiss = () => setOpen(false)

    return toastFn
  }, [baseShowToast])

  const handleClose = (event: React.SyntheticEvent | Event | null, reason?: string) => {
    if (reason === 'clickaway') return
    setOpen(false)
  }

  // Pausa en hover
  const handleMouseEnter = () => setTimer(null)
  const handleMouseLeave = () => setTimer(toastConfig.duration)

  // Parsear la posición "horizontal_vertical" con adaptación en móvil
  const getAnchorOrigin = (pos?: ToastPosition): SnackbarOrigin => {
    if (isMobile) {
      return { vertical: 'bottom', horizontal: 'center' }
    }
    if (!pos) return { vertical: 'bottom', horizontal: 'right' }
    const [horizontal, vertical] = pos.split('_') as [HorizontalPos, VerticalPos]
    return { horizontal, vertical }
  }

  // Elegir la animación de Slide basada en la posición
  const transitionComponent = useMemo(() => {
    if (isMobile) return SlideTransitionUp
    const pos = toastConfig.position || 'right_bottom'
    const [horizontal, vertical] = pos.split('_')

    if (horizontal === 'right') return SlideTransitionLeft
    if (horizontal === 'left') return SlideTransitionRight
    if (vertical === 'top') return SlideTransitionDown
    return SlideTransitionUp
  }, [toastConfig.position, isMobile])

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      <Snackbar
        open={open}
        autoHideDuration={timer}
        onClose={handleClose}
        anchorOrigin={getAnchorOrigin(toastConfig.position)}
        slots={{
          transition: transitionComponent,
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        sx={{
          zIndex: 100000,
          ...(isMobile && {
            bottom: 'max(16px, env(safe-area-inset-bottom, 0px)) !important',
            left: '16px !important',
            right: '16px !important',
          }),
        }}
      >
        <Alert
          onClose={handleClose}
          severity={toastConfig.severity}
          variant="filled"
          elevation={8}
          action={
            toastConfig.action ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {toastConfig.action}
                <IconButton size="small" aria-label="close" color="inherit" onClick={handleClose}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            ) : undefined
          }
          sx={{
            width: '100%',
            minWidth: { xs: '260px', sm: '300px' },
            maxWidth: { xs: '100%', sm: '420px' },
            wordBreak: 'break-word',
            borderRadius: (theme) => theme.shape.borderRadius,
            color: '#FFFFFF !important',
            fontWeight: 500,
            boxShadow: (th) => th.shadows[8],
            '& .MuiAlert-icon': {
              color: '#FFFFFF !important',
              fontSize: '22px',
            },
            '& .MuiAlert-action': {
              color: '#FFFFFF !important',
            },
            '& .MuiAlert-message': {
              color: '#FFFFFF !important',
            },
          }}
        >
          {toastConfig.title && (
            <AlertTitle
              sx={{
                fontWeight: 700,
                fontSize: '0.95rem',
                mb: 0.25,
                color: '#FFFFFF !important',
              }}
            >
              {toastConfig.title}
            </AlertTitle>
          )}
          {toastConfig.message}
        </Alert>
      </Snackbar>
    </ToastContext.Provider>
  )
}
